import type { AdminEvent, BackendEvent, BackendEventStatus, EventApplication, EventStatus, EventTag, Tier } from './types'
import { apiFetch } from '@/lib/api'

type Envelope<T> = { success: true; data: T }

function stableNumericId(id: string) {
  // Derive a stable small-ish number from the Mongo ObjectId.
  const hex = String(id || '').slice(-8)
  const n = Number.parseInt(hex, 16)
  return Number.isFinite(n) ? n : Date.now()
}

function mapStatus(status: BackendEventStatus): { status: EventStatus; tag: EventTag } {
  if (status === 'draft') return { status: 'draft', tag: 'drafts' }
  if (status === 'archived') return { status: 'archived', tag: 'past' }

  // published / closed => active in legacy UI
  return { status: 'active', tag: 'upcoming' }
}

function computeTagFromStartsAt(startsAt: string | null, fallback: EventTag) {
  if (!startsAt) return fallback
  const t = new Date(startsAt).getTime()
  if (Number.isNaN(t)) return fallback
  return t < Date.now() ? 'past' : 'upcoming'
}

function computeFillRate(current: number, total: number) {
  if (!total || total <= 0) return 0
  return Math.max(0, Math.min(100, Math.round((current / total) * 100)))
}

const DEFAULT_TIER: Tier = 'Recruit'

function toAdminEvent(raw: BackendEvent): AdminEvent {
  const base = mapStatus(raw.status)
  const tag = computeTagFromStartsAt(raw.startsAt, base.tag)
  const tier =
    raw.tierRequirement && ['Recruit', 'Verified', 'Proven', 'Family'].includes(String(raw.tierRequirement))
      ? (String(raw.tierRequirement) as Tier)
      : DEFAULT_TIER
  const total = raw.capacity ?? 0
  const current = raw.approvedCount ?? 0

  const cover =
    typeof raw.coverImage === 'string' && raw.coverImage.trim() !== '' ? raw.coverImage.trim() : ''

  return {
    _id: raw._id,
    id: stableNumericId(raw._id),
    title: raw.title,
    slug: raw.slug ?? '',
    city: raw.city ?? '',
    venue: raw.venueName ?? '',
    startsAt: raw.startsAt ?? new Date().toISOString(),
    coverImage: cover,
    gradient: 'from-slate-600 via-slate-700 to-slate-900',
    tier,
    rsvps: { current, total },
    rewards: Number(raw.rewardsEarned || 0),
    type: raw.eventType ?? '',
    fillRate: computeFillRate(current, total),
    tag,
    status: base.status,
    raw,
  }
}

export type AdminEventsCounts = { all: number; draft: number; upcoming: number; past: number }

export type ListEventsParams = {
  tag?: EventTag | 'all'
  search?: string
  cities?: string[]
  tiers?: Tier[]
  types?: string[]
}

export async function listEventsRequest(params: ListEventsParams = {}) {
  const qs = new URLSearchParams()
  if (params.tag && params.tag !== 'all') qs.set('tag', params.tag)
  if (params.search?.trim()) qs.set('search', params.search.trim())
  if (params.cities?.length) qs.set('cities', params.cities.join(','))
  if (params.tiers?.length) qs.set('tiers', params.tiers.join(','))
  if (params.types?.length) qs.set('types', params.types.join(','))

  const url = qs.toString() ? `/admin/events?${qs.toString()}` : '/admin/events'
  const { data } = await apiFetch<Envelope<{ events: BackendEvent[]; counts: AdminEventsCounts }>>(url)
  return { events: data.events.map(toAdminEvent), counts: data.counts }
}

export type EventCountsParams = {
  search?: string
  cities?: string[]
  tiers?: Tier[]
  types?: string[]
}

export async function fetchEventCountsRequest(params: EventCountsParams = {}) {
  const qs = new URLSearchParams()
  if (params.search?.trim()) qs.set('search', params.search.trim())
  if (params.cities?.length) qs.set('cities', params.cities.join(','))
  if (params.tiers?.length) qs.set('tiers', params.tiers.join(','))
  if (params.types?.length) qs.set('types', params.types.join(','))

  const url = qs.toString() ? `/admin/events/counts?${qs.toString()}` : '/admin/events/counts'
  const { data } = await apiFetch<Envelope<{ counts: AdminEventsCounts }>>(url)
  return data.counts
}

export async function getEventByIdRequest(id: string) {
  const { data } = await apiFetch<Envelope<{ event: BackendEvent }>>(`/admin/events/${encodeURIComponent(id)}`)
  return toAdminEvent(data.event)
}

export async function getEventBySlugRequest(slug: string) {
  const { data } = await apiFetch<Envelope<{ event: BackendEvent }>>(`/admin/events/slug/${encodeURIComponent(slug)}`)
  return toAdminEvent(data.event)
}

export async function createEventRequest(payload: Record<string, unknown>) {
  const { data } = await apiFetch<Envelope<{ event: BackendEvent }>>('/admin/events', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return toAdminEvent(data.event)
}

export async function updateEventRequest(id: string, patch: Record<string, unknown>) {
  const { data } = await apiFetch<Envelope<{ event: BackendEvent }>>(`/admin/events/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
  return toAdminEvent(data.event)
}

export async function deleteEventRequest(id: string) {
  const { data } = await apiFetch<Envelope<{ event: BackendEvent }>>(`/admin/events/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  return toAdminEvent(data.event)
}

export async function uploadCoverImageRequest(file: File) {
  const body = new FormData()
  body.append('file', file)
  const { data } = await apiFetch<Envelope<{ url: string; key: string }>>('/admin/uploads', {
    method: 'POST',
    body,
  })
  return data
}

// ── Application management ──

export async function listApplicationsRequest(eventId: string) {
  const { data } = await apiFetch<Envelope<{ applications: EventApplication[] }>>(
    `/admin/events/${encodeURIComponent(eventId)}/applications`
  )
  return data.applications
}

export async function updateApplicationStatusRequest(
  applicationId: string,
  payload: { status: string; notes?: string; force?: boolean }
) {
  const { data } = await apiFetch<Envelope<{ application: EventApplication }>>(
    `/admin/applications/${encodeURIComponent(applicationId)}/status`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  )
  return data.application
}

export type BulkUpdateResult = {
  updated: number
  autoWaitlisted: number
  skipped: { id: string; reason: string }[]
}

export async function bulkUpdateApplicationStatusRequest(
  payload: { applicationIds: string[]; status: string; notes?: string }
) {
  const { data } = await apiFetch<Envelope<BulkUpdateResult>>(
    '/admin/applications/bulk',
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  )
  return data
}

// ── Direct invite ──

export interface SearchUser {
  _id: string
  name?: string
  handle?: string
  email: string
  phone?: string
  avatarUrl?: string | null
  rewardsLifetime?: number
  role?: string
}

export async function searchUsersRequest(q: string) {
  const { data } = await apiFetch<Envelope<{ users: SearchUser[] }>>(
    `/admin/users/search?q=${encodeURIComponent(q)}`
  )
  return data.users
}

export async function directInviteRequest(
  eventId: string,
  payload: { userId: string; personalNote?: string }
) {
  const { data } = await apiFetch<Envelope<{ application: EventApplication; autoWaitlisted: boolean }>>(
    `/admin/events/${encodeURIComponent(eventId)}/invitations`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  )
  return data
}

// ── Messaging ──

export type SendMessagePayload = {
  recipients: string | string[]  // status bucket string OR array of applicationIds
  subject: string
  body: string
}

export type SendMessageResult = {
  sent: number
  failed: { userId: string; reason: string }[]
}

export async function sendEventMessageRequest(eventId: string, payload: SendMessagePayload) {
  const { data } = await apiFetch<Envelope<SendMessageResult>>(
    `/admin/events/${encodeURIComponent(eventId)}/messages`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  )
  return data
}

// ── Check-in ──

export type AttendeeType = 'creator' | 'operator' | 'other'

export interface CheckInAttendee {
  applicationId: string
  name: string | null
  handle: string | null
  avatarUrl: string | null
  email: string | null
  phone: string | null
  rewardsLifetime: number
  status: 'approved' | 'checked_in'
  checkedInAt: string | null
  rewardsAwarded: number
  isGuest: boolean
  isWalkIn: boolean
  source?: string
  attendeeType: AttendeeType | null
  adminNotes: string
  onWaitlist: boolean
}

export interface CheckInResult {
  application: { applicationId: string; status: string; checkedInAt: string | null; rewardsAwarded: number }
  rewardsAwarded: number
  newLifetimeTotal: number
  transactionId: string | null
  duplicateWarning: boolean
}

export interface UncheckInResult {
  application: { applicationId: string; status: string; checkedInAt: null; rewardsAwarded: 0 }
  rewardsReversed: number
  newLifetimeTotal: number
}

export async function checkInSearchRequest(eventId: string, q?: string) {
  const qs = q ? `?q=${encodeURIComponent(q)}` : ''
  const { data } = await apiFetch<Envelope<{ attendees: CheckInAttendee[] }>>(
    `/admin/events/${encodeURIComponent(eventId)}/check-in/search${qs}`
  )
  return data.attendees
}

export async function checkInRequest(applicationId: string) {
  const { data } = await apiFetch<Envelope<CheckInResult>>(
    `/admin/applications/${encodeURIComponent(applicationId)}/check-in`,
    { method: 'POST' }
  )
  return data
}

export async function uncheckInRequest(applicationId: string) {
  const { data } = await apiFetch<Envelope<UncheckInResult>>(
    `/admin/applications/${encodeURIComponent(applicationId)}/uncheck-in`,
    { method: 'POST' }
  )
  return data
}

export async function addWalkInRequest(eventId: string, payload: { name: string; email: string; phone?: string }) {
  const { data } = await apiFetch<Envelope<{ applicationId: string; duplicateWarning: boolean }>>(
    `/admin/events/${encodeURIComponent(eventId)}/walk-ins`,
    { method: 'POST', body: JSON.stringify(payload) }
  )
  return data
}

export async function updateAttendeeTypeRequest(applicationId: string, attendeeType: AttendeeType | null) {
  const { data } = await apiFetch<Envelope<{ attendeeType: AttendeeType | null }>>(
    `/admin/applications/${encodeURIComponent(applicationId)}/attendee-type`,
    { method: 'PATCH', body: JSON.stringify({ attendeeType }) }
  )
  return data
}

export async function updateAdminNotesRequest(applicationId: string, adminNotes: string) {
  const { data } = await apiFetch<Envelope<{ adminNotes: string }>>(
    `/admin/applications/${encodeURIComponent(applicationId)}/admin-notes`,
    { method: 'PATCH', body: JSON.stringify({ adminNotes }) }
  )
  return data
}

export async function updateAttendeeDetailsRequest(
  applicationId: string,
  payload: { name?: string; email?: string; phone?: string }
) {
  const { data } = await apiFetch<Envelope<unknown>>(
    `/admin/applications/${encodeURIComponent(applicationId)}/details`,
    { method: 'PATCH', body: JSON.stringify(payload) }
  )
  return data
}

